import { LucideIcon } from 'lucide-react';
import { TiltCard } from './marketing/TiltCard';

interface UseCaseCardProps {
  title: string;
  short: string;
  icon: LucideIcon;
  color: 'blue' | 'red' | 'green' | 'purple' | 'orange' | 'teal' | 'indigo' | 'cyan';
  onClick: () => void;
}

const colorStyles = {
  blue: {
    gradient: 'from-blue-500 to-blue-600',
    bgLight: 'bg-blue-100/90 dark:bg-blue-950/75 backdrop-blur-sm',
    borderLight: 'border-blue-200 dark:border-blue-800',
    textLight: 'text-blue-700 dark:text-blue-300',
    circleLight: 'bg-blue-200 dark:bg-blue-900'
  },
  red: {
    gradient: 'from-red-500 to-red-600',
    bgLight: 'bg-red-100/90 dark:bg-red-950/75 backdrop-blur-sm',
    borderLight: 'border-red-200 dark:border-red-800',
    textLight: 'text-red-700 dark:text-red-300',
    circleLight: 'bg-red-200 dark:bg-red-900'
  },
  green: {
    gradient: 'from-green-500 to-green-600',
    bgLight: 'bg-green-100/90 dark:bg-green-950/75 backdrop-blur-sm',
    borderLight: 'border-green-200 dark:border-green-800',
    textLight: 'text-green-700 dark:text-green-300',
    circleLight: 'bg-green-200 dark:bg-green-900'
  },
  purple: {
    gradient: 'from-purple-500 to-purple-600',
    bgLight: 'bg-purple-100/90 dark:bg-purple-950/75 backdrop-blur-sm',
    borderLight: 'border-purple-200 dark:border-purple-800',
    textLight: 'text-purple-700 dark:text-purple-300',
    circleLight: 'bg-purple-200 dark:bg-purple-900'
  },
  orange: {
    gradient: 'from-orange-500 to-orange-600',
    bgLight: 'bg-orange-100/90 dark:bg-orange-950/75 backdrop-blur-sm',
    borderLight: 'border-orange-200 dark:border-orange-800',
    textLight: 'text-orange-700 dark:text-orange-300',
    circleLight: 'bg-orange-200 dark:bg-orange-900'
  },
  teal: {
    gradient: 'from-teal-500 to-teal-600',
    bgLight: 'bg-teal-100/90 dark:bg-teal-950/75 backdrop-blur-sm',
    borderLight: 'border-teal-200 dark:border-teal-800',
    textLight: 'text-teal-700 dark:text-teal-300',
    circleLight: 'bg-teal-200 dark:bg-teal-900'
  },
  indigo: {
    gradient: 'from-indigo-500 to-indigo-600',
    bgLight: 'bg-indigo-100/90 dark:bg-indigo-950/75 backdrop-blur-sm',
    borderLight: 'border-indigo-200 dark:border-indigo-800',
    textLight: 'text-indigo-700 dark:text-indigo-300',
    circleLight: 'bg-indigo-200 dark:bg-indigo-900'
  },
  cyan: {
    gradient: 'from-cyan-500 to-cyan-600',
    bgLight: 'bg-cyan-100/90 dark:bg-cyan-950/75 backdrop-blur-sm',
    borderLight: 'border-cyan-200 dark:border-cyan-800',
    textLight: 'text-cyan-700 dark:text-cyan-300',
    circleLight: 'bg-cyan-200 dark:bg-cyan-900'
  }
};

export function UseCaseCard({ title, short, icon: Icon, color, onClick }: UseCaseCardProps) {
  const styles = colorStyles[color];

  return (
    <TiltCard
      onClick={onClick}
      className={`group relative cursor-pointer ${styles.bgLight} ${styles.borderLight} border rounded-xl p-6 shadow-lg hover:shadow-2xl transition-shadow duration-200 h-full flex flex-col`}
    >
      {/* Decorative Corner Circles */}
      <div className={`absolute top-3 right-3 w-2 h-2 rounded-full ${styles.circleLight} opacity-60`}></div>
      <div className={`absolute bottom-3 left-3 w-1.5 h-1.5 rounded-full ${styles.circleLight} opacity-40`}></div>

      {/* Icon + Title Row */}
      <div className="flex items-center gap-3 mb-3">
        {/* Icon */}
        <div className={`flex-shrink-0 p-2 rounded bg-gradient-to-br ${styles.gradient} shadow-md`}>
          <Icon className="w-5 h-5 text-white" />
        </div>

        {/* Title */}
        <h3 className={`text-xl font-bold ${styles.textLight}`}>
          {title}
        </h3>
      </div>

      {/* Short Description with grow to ensure consistent card height */}
      <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed grow">
        {short}
      </p>

      {/* Click Indicator */}
      <div className={`absolute bottom-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-200 ${styles.textLight}`}>
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </div>
    </TiltCard>
  );
}
